import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, ShieldCheck, MapPin } from "lucide-react";
import { BRAND, NAV_LINKS, SERVICES } from "../lib/constants";

export const Footer = () => {
  const year = new Date().getFullYear();
  const featured = SERVICES.slice(0, 8);

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[color:var(--isa-navy)] text-white"
    >
      <div className="absolute inset-0 pattern-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center">
                <img
                  src={BRAND.logoShield}
                  alt={BRAND.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-serif font-semibold text-xl text-white tracking-tight">
                  Insurances Solutions
                </span>
                <span className="font-sans font-bold text-[10px] uppercase tracking-[0.32em] text-[color:var(--isa-gold)]">
                  Agency, LLC
                </span>
              </div>
            </div>
            <p className="mt-6 text-sm font-sans text-white/70 leading-relaxed max-w-sm">
              Protecting individuals, families, and business owners with personalized
              insurance and financial solutions, backed by a strong foundation in tax
              and financial services.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center border border-white/20 hover:border-[color:var(--isa-gold)] hover:text-[color:var(--isa-gold)] transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.6} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-[color:var(--isa-gold)]">Navigate</p>
            <ul className="mt-6 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    className="text-sm font-sans text-white/80 hover:text-[color:var(--isa-gold)] transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-[color:var(--isa-gold)]">Coverage</p>
            <ul className="mt-6 grid grid-cols-1 gap-3">
              {featured.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-sm font-sans text-white/80 hover:text-[color:var(--isa-gold)] transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow text-[color:var(--isa-gold)]">Connect</p>
            <ul className="mt-6 space-y-4">
              <li>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  className="flex items-start gap-3 text-sm font-sans text-white/85 hover:text-[color:var(--isa-gold)] transition-colors"
                  data-testid="footer-phone-link"
                >
                  <Phone className="w-4 h-4 mt-0.5" strokeWidth={1.6} />
                  <span>{BRAND.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-start gap-3 text-sm font-sans text-white/85 hover:text-[color:var(--isa-gold)] transition-colors break-all"
                  data-testid="footer-email-link"
                >
                  <Mail className="w-4 h-4 mt-0.5" strokeWidth={1.6} />
                  <span>{BRAND.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={BRAND.addressMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm font-sans text-white/85 hover:text-[color:var(--isa-gold)] transition-colors"
                  data-testid="footer-address-link"
                >
                  <MapPin className="w-4 h-4 mt-0.5" strokeWidth={1.6} />
                  <span>{BRAND.address}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-xs font-sans text-white/55 uppercase tracking-[0.18em]">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-[color:var(--isa-gold)]" strokeWidth={1.6} />
                <span>Licensed & Independent</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs font-sans text-white/50">
            © {year} {BRAND.name}. All rights reserved.
          </p>
          <p className="text-xs font-sans text-white/40">
            Insurance offered through independent licensed agents. Coverage subject to underwriting and policy terms.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
